/**
 * « Entraînement » — ce qui se lit sans DOM.
 *
 * Hedi, le 5 octobre 2026 : l'élève de lycée compose ses propres entraînements,
 * depuis ses cahiers ou par génération, les travaille sur papier en
 * photographiant chaque exercice, et **les clôt quand il veut voir la
 * correction**. Les feuilles sont partagées avec ses parents, qui les lisent et
 * jugent ce qui a été photographié.
 *
 * Ici vivent les sous-onglets, le partage entre feuille en cours et feuilles
 * closes, la phrase de clôture et le verdict en mots — tout ce qu'un test
 * vérifie sans monter d'écran.
 */

import type { EntrainementOut, LigneDEntrainementOut } from "@/api/schema";
import type { SousOnglet } from "@/ui/SousOnglets";

/** L'adresse de l'onglet. Aucun identifiant d'enfant (invariant 2) : le
 *  serveur lit l'élève dans la session. */
export const MON_ENTRAINEMENT = "/eleve/entrainement";

/**
 * Les quatre sous-onglets.
 *
 * « En cours » est `end` : « Générer », « Cahiers » et « Terminés » sont sous
 * son adresse, et sans `end` il resterait allumé sur les trois. « Cahiers » ne
 * l'est pas, ses deux niveaux (`/:ouvrage`, `/:ouvrage/:cle`) étant dessous.
 */
export function ongletsDeMonEntrainement(): SousOnglet[] {
  return [
    { to: MON_ENTRAINEMENT, label: "En cours", end: true },
    { to: `${MON_ENTRAINEMENT}/generer`, label: "Générer" },
    { to: `${MON_ENTRAINEMENT}/cahiers`, label: "Cahiers" },
    { to: `${MON_ENTRAINEMENT}/termines`, label: "Terminés" },
  ];
}

/**
 * La feuille en cours, et les closes de la plus récente à la plus ancienne.
 *
 * Le serveur les envoie déjà dans cet ordre-là (la feuille ouverte d'abord) ;
 * on retrie quand même les closes, parce que « Terminés » dit « la plus récente
 * en tête » et qu'un ordre tenu par un autre fichier, dans une autre langue,
 * finit par changer sans que cet écran le sache. Il n'y a au plus qu'une
 * feuille ouverte par origine — un index unique en base le garantit.
 */
export function partagerMesFeuilles(feuilles: readonly EntrainementOut[]): {
  enCours: EntrainementOut | null;
  closes: EntrainementOut[];
} {
  const enCours = feuilles.find((feuille) => !feuille.closed_at) ?? null;
  const closes = feuilles
    .filter((feuille) => Boolean(feuille.closed_at))
    .sort((a, b) => (b.closed_at ?? "").localeCompare(a.closed_at ?? ""));
  return { enCours, closes };
}

/**
 * La phrase qui précède la clôture, dite dans la page.
 *
 * Elle dit **ce que le geste emporte**, et non « tu confirmes ? » : tous les
 * corrigés s'ouvrent, c'est sans retour, et les photos ne pourront plus être
 * envoyées — une copie prise après avoir lu le corrigé n'est plus une
 * tentative, et le serveur la refuse. Les exercices encore sans photo sont
 * comptés, parce que c'est la seule chose que l'élève peut encore réparer
 * avant d'appuyer.
 */
export function confirmationDeCloture(lignes: number, photographiees: number): string {
  const corriges =
    lignes > 1
      ? `Tu vas voir les corrigés de ces ${lignes} exercices.`
      : "Tu vas voir le corrigé de cet exercice.";
  const retour =
    "C'est sans retour : la feuille ne se rouvre pas, et tu ne pourras plus envoyer de photo.";
  const sansPhoto = lignes - photographiees;
  if (sansPhoto <= 0) return `${corriges} ${retour}`;
  const reste =
    sansPhoto === 1
      ? "Un exercice n'a pas encore de photo : sans elle, ton parent ne pourra le compter que « pas fait »."
      : `${sansPhoto} exercices n'ont pas encore de photo : sans elle, ton parent ne pourra les compter que « pas fait ».`;
  return `${corriges} ${retour} ${reste}`;
}

/**
 * Le verdict du parent, en mots.
 *
 * Sans score ni note : un mot par verdict, celui que le parent a choisi. Une
 * ligne sans verdict n'est pas « fausse », elle n'est **pas encore relue**, et
 * l'écran le dit.
 */
const VERDICTS: Record<NonNullable<LigneDEntrainementOut["verdict"]>, string> = {
  juste: "juste",
  moitie: "à moitié juste",
  faux: "à reprendre",
  pas_fait: "pas fait",
};

export function verdictEnMots(ligne: Pick<LigneDEntrainementOut, "verdict">): string {
  if (!ligne.verdict) return "pas encore relu par ton parent";
  return `Ton parent : ${VERDICTS[ligne.verdict] ?? ligne.verdict}`;
}
